import type { UserAnswersType } from './types';
import { ExitIntentDialogReturnType } from '../exitIntentDialog';

import { adjustMainContentsHeight } from './functional/adjustMainContentsHeight';
import { userSegment } from './functional/userSegment';
import { hideLoading } from '../utils';

// bundles
import { roofDeteriorationBundle } from './sectionBundles/roofDeterioration';
import { wallDeteriorationBundle } from './sectionBundles/wallDeterioration';

// sections
import { contractorSearchInterfaceSection } from './sections/contractorSearchInterface';
import { buildingTypeSection } from './sections/buildingType';
import { constructionPartsSection } from './sections/constructionParts';
import { howManyStoriesSection } from './sections/howManyStories';
import { totalFloorSpaceSection } from './sections/totalFloorSpace';
import { roofShapeSection } from './sections/roofShape';
import { roofInclinationSection } from './sections/roofInclination';
import { regionSection } from './sections/region';
import { prefectureSection } from './sections/prefecture';
import { ageOfBuildingSection } from './sections/ageOfBuilding';
import { roofingMaterialSection } from './sections/roofingMaterial';
import { roofConstructionTypeSection } from './sections/roofConstructionType';
import { wallMaterialSection } from './sections/wallMaterial';
import { wallConstructionTypeSection } from './sections/wallConstructionType';
import { desiredPeriodSection } from './sections/desiredPeriod';
import { alreadyHaveEstimateSection } from './sections/alreadyHaveEstimate';
import { citySection } from './sections/city';
import { lastMessageSection } from './sections/lastMessage';
import { preferredSearchCriteriaSection } from './sections/preferredSearchCriteria';
import { wordsOfInterestSection } from './sections/wordsOfInterest';
import { ctaButtonSection } from './sections/ctaButton';

type ChatPropsType = {
  beforeUnloadHandler: ExitIntentDialogReturnType['beforeUnloadHandler'];
}

export const chat = async ({ beforeUnloadHandler }: ChatPropsType) => {
  const chatRoot = document.querySelector<HTMLDivElement>('.chat-root');
  const userAnswers: UserAnswersType = [];
  const commonAnswerHandler = (paramName: string, answerText: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ [paramName]: answerText });
  };
  const afterFirstAnswer = () => {
    // 1つ目の回答後にフッターを非表示にする
    const footerElement = document.querySelector<HTMLElement>('.footer');
    footerElement.classList.add('is-hidden');
    footerElement.style.display = '';
  };
  const sectionProps = {
    chatRoot,
    userAnswers,
    commonAnswerHandler,
    afterFirstAnswer,
    beforeUnloadHandler,
  };
  const { selectedSearchCriteriaUser } = userSegment(userAnswers);

  // チャットの初期処理
  new Promise<void>((resolve) => {
    // ページ表示
    document.querySelector<HTMLElement>('.proposals-new').classList.add('is-show');

    // スマホ時：ページの高さ設定する処理
    adjustMainContentsHeight();

    hideLoading();

    resolve();
  });

  // オープニングページ
  await contractorSearchInterfaceSection(sectionProps);

  const prefectureAnswerData = userAnswers.find((answer) => answer.keyName === 'prefecture');
  if (!prefectureAnswerData) {
    // 地域
    await regionSection(sectionProps);

    // 都道府県
    await prefectureSection(sectionProps);
  }

  // 市区町村
  await citySection(sectionProps);

  if (!selectedSearchCriteriaUser()) {
    // 重視したい項目
    await preferredSearchCriteriaSection(sectionProps);
  }

  const constructionCategoryAnswerData = userAnswers.find((answer) => answer.keyName === 'constructionCategory');
  if (!constructionCategoryAnswerData) {
    // 工事箇所
    const constructionPartsAnswer = await constructionPartsSection(sectionProps);

    // 外壁の工事種別
    await wallConstructionTypeSection(sectionProps);

    if (constructionPartsAnswer.answerText === '外壁と屋根') {
      // 屋根の工事種別
      await roofConstructionTypeSection(sectionProps);
    }
  }

  // 物件種別
  await buildingTypeSection(sectionProps);

  // 何階建て
  await howManyStoriesSection(sectionProps);

  // 延べ床面積
  await totalFloorSpaceSection(sectionProps);

  const constructionPartsAnswerData = userAnswers.find((answer) => answer.keyName === 'constructionParts');
  if (constructionPartsAnswerData.answer === '外壁と屋根') {
    // 屋根の形
    const roofShapeAnswer = await roofShapeSection(sectionProps);

    // 屋根の傾斜
    if (roofShapeAnswer.answerText !== '陸屋根') {
      await roofInclinationSection(sectionProps);
    }
  }

  // 築年数
  await ageOfBuildingSection(sectionProps);

  // 現在の外壁材
  await wallMaterialSection(sectionProps);

  const wallConstructionTypeAnswerData = userAnswers.find((answer) => answer.keyName === 'wallConstructionType');
  const searchByDeteriorationAnswerData = userAnswers.find((answer) => answer.keyName === 'searchByDeterioration');
  if (wallConstructionTypeAnswerData.answer === '決まっていない' && !searchByDeteriorationAnswerData) {
    // 外壁の劣化診断バンドル
    await wallDeteriorationBundle(sectionProps);
  }

  if (constructionPartsAnswerData.answer === '外壁と屋根') {
    // 現在の屋根材
    await roofingMaterialSection(sectionProps);

    const roofConstructionTypeAnswerData = userAnswers.find((answer) => answer.keyName === 'roofConstructionType');
    if (roofConstructionTypeAnswerData.answer === '決まっていない' && !searchByDeteriorationAnswerData) {
      // 屋根の劣化診断バンドル
      await roofDeteriorationBundle(sectionProps);
    }
  }

  // 工事時期
  await desiredPeriodSection(sectionProps);

  // 見積もりの有無
  await alreadyHaveEstimateSection(sectionProps);

  // 他に気になるキーワード
  await wordsOfInterestSection(sectionProps);

  // 最終メッセージ
  await lastMessageSection(sectionProps);

  // CTAボタン
  await ctaButtonSection(sectionProps);
};
